import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";

export interface UnlockMonthInput {
  userId: string;
  year: number;
  month: number;
  reason: string;
}

/**
 * Admin-opplåsing av en attestert (låst) måned for en ansatt (VIB-1246 Fase 2).
 *
 * Reverserer månedslåsen ved å fjerne monthly_confirmations-raden via den
 * SECURITY DEFINER, is_admin()-gatede RPC-en admin_unlock_month. Sletting er
 * revoket fra `authenticated`, så dette er eneste vei en ikke-system-bruker kan
 * låse opp, og handlingen audit-logges (snapshot) til event_log med aktør.
 */
export function useUnlockMonth() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, year, month, reason }: UnlockMonthInput) => {
      const { error } = await supabase.rpc("admin_unlock_month", {
        p_user_id: userId,
        p_year: year,
        p_month: month,
        p_reason: reason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: async (_data, { userId }) => {
      await invalidateFor(queryClient, "monthly_confirmations", { userId });
      toast.success("Måneden er gjenåpnet");
    },
    onError: (error) => {
      toast.error((error as Error).message || "Kunne ikke låse opp måneden");
    },
  });
}
